import { useState } from "react";
import axios from "axios";
import {
  Handshake,
  Building2,
  ArrowRight,
  Loader2,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useContent, resolveImage } from "../contexts/ContentContext";
import { Donate } from "./Donate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ways = [
  {
    icon: Handshake,
    title: "Volunteer",
    text: "Tutor, mentor, drive a field trip, or lead a workshop — share what you know with young people who need your voice.",
    cta: "Volunteer with us",
    interest: "volunteer",
    accent: "bg-[#2563EB]",
    accentText: "text-white",
  },
  {
    icon: Building2,
    title: "Partner",
    text: "Bring your business, school, or organization alongside the foundation as a long-term program partner.",
    cta: "Become a partner",
    interest: "partner",
    accent: "bg-[#FFD100]",
    accentText: "text-[#0F172A]",
  },
  {
    icon: MessageCircle,
    title: "Stay Connected",
    text: "Just want to learn more? Share an idea, ask a question, or follow the journey — we'd love to hear from you.",
    cta: "Send a message",
    interest: "general",
    accent: "bg-[#1E3A8A]",
    accentText: "text-white",
  },
];

export const GetInvolved = () => {
  const { data: contact } = useContent("contact");
  const contactEmail = contact?.email || "info@semajmorgan.org";
  const contactAddress = contact?.address || "Detroit & Farmington Hills, Michigan";
  const contactImage = resolveImage(contact?.image_url || "");
  const contactImageChip = contact?.image_chip || "Local 4 News";
  const [form, setForm] = useState({
    name: "",
    email: "",
    interest: "volunteer",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) =>
    setForm((s) => ({ ...s, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", interest: form.interest, message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const setInterest = (interest) => setForm((s) => ({ ...s, interest }));

  return (
    <section
      id="get-involved"
      data-testid="get-involved-section"
      className="py-20 md:py-28 bg-slate-50 relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#2563EB]/10 blur-3xl rounded-full"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <span className="section-eyebrow">Get Involved</span>
          <h2
            data-testid="get-involved-heading"
            className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900"
          >
            Three ways to <span className="text-gradient-brand">step in</span>.
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-600">
            Every hour, every partnership, every conversation multiplies into
            opportunity for a young person.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {ways.map((w) => {
            const Icon = w.icon;
            return (
              <button
                type="button"
                key={w.title}
                data-testid={`get-involved-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => {
                  setInterest(w.interest);
                  document
                    .getElementById("contact-form-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="card-soft text-left p-7 md:p-8 group"
              >
                <span
                  className={`inline-flex w-14 h-14 rounded-2xl ${w.accent} ${w.accentText} items-center justify-center`}
                >
                  <Icon className="w-7 h-7" strokeWidth={2.2} />
                </span>
                <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">
                  {w.title}
                </h3>
                <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
                  {w.text}
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] group-hover:gap-3 transition-all">
                  {w.cta} <ArrowRight className="w-4 h-4" />
                </span>
              </button>
            );
          })}
        </div>

        {/* Donate (Cash App + Zelle QR codes) */}
        <Donate />

        {/* Contact form */}
        <div
          id="contact-form-anchor"
          data-testid="contact-form-section"
          className="mt-16 grid lg:grid-cols-12 gap-10 items-start"
        >
          <div className="lg:col-span-5">
            <span className="section-eyebrow">Reach out</span>
            <h3 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Tell us how you'd like to help.
            </h3>

            <div
              data-testid="contact-photo"
              className="mt-6 relative rounded-3xl overflow-hidden ring-1 ring-slate-100 shadow-[0_20px_60px_rgba(15,23,42,0.18)] aspect-[4/3]"
            >
              {contactImage ? (
                <img
                  src={contactImage}
                  alt="Semaj J. Morgan Foundation"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 30%" }}
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#1E3A8A] to-[#2563EB]" />
              )}
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
              {contactImageChip && (
                <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 bg-[#FFD100] text-[#0F172A] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {contactImageChip}
                  </span>
                </div>
              )}
            </div>

            <p className="mt-5 text-slate-600 max-w-md">
              Drop us a line and a member of our team will follow up within
              two business days.
            </p>
            <div className="mt-8 space-y-3">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">Email: </span>
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-[#2563EB] hover:underline"
                  data-testid="contact-email-link"
                >
                  {contactEmail}
                </a>
              </p>
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">Office: </span>
                {contactAddress}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="card-soft p-6 md:p-8 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="text-xs font-bold uppercase tracking-wider text-slate-500"
                  >
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 focus:border-[#2563EB] transition"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="text-xs font-bold uppercase tracking-wider text-slate-500"
                  >
                    Email
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 focus:border-[#2563EB] transition"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  I'm interested in
                </label>
                <div
                  className="mt-2 flex flex-wrap gap-2"
                  role="radiogroup"
                  aria-label="Interest"
                >
                  {[
                    { v: "volunteer", l: "Volunteering" },
                    { v: "partner", l: "Partnering" },
                    { v: "general", l: "General" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      role="radio"
                      aria-checked={form.interest === opt.v}
                      data-testid={`contact-interest-${opt.v}`}
                      onClick={() => setInterest(opt.v)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                        form.interest === opt.v
                          ? "bg-[#2563EB] text-white border-[#2563EB]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#2563EB]/40"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className="text-xs font-bold uppercase tracking-wider text-slate-500"
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us a bit about how you'd like to engage…"
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 focus:border-[#2563EB] transition resize-none"
                />
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-xs text-slate-500">
                  We respect your privacy. No spam, ever.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : submitted ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Sent — send another?
                    </>
                  ) : (
                    <>
                      Send message <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
